/* ──────────────────────────────────────────────────────────────
   CommandPalette/index.tsx · global command palette (⌘K)
   ────────────────────────────────────────────────────────────── */
import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  TouchableWithoutFeedback,
  Modal,
  ScrollView,
  Platform,
  ViewStyle,
  TextStyle,
} from 'react-native';
import { useTheme } from '../context/themeContext';
import { Icon, StateBadge, Thumb, CommonSearch } from '../components/ui';
import { searchItems } from '../services/catalog/search';
import { navigateApp } from '../navigation/navigationRef';
import type { AppRouteName, AppStackParamList } from '../navigation/types';

interface ItemHit { id: string; name: string; ref: string; state?: string }

interface NavEntry {
  label: string;
  view: AppRouteName;
  params?: AppStackParamList[AppRouteName];
  icon: string;
}

const NAV_ITEMS: NavEntry[] = [
  { label: 'Dashboard', view: 'dashboard', icon: 'home' },
  { label: 'Items', view: 'items', icon: 'box' },
  { label: 'New item', view: 'editor', params: { id: 'new', type: 'ty_menu' }, icon: 'plus' },
  { label: 'Bundles', view: 'bundles', icon: 'layers' },
  { label: 'Bundle price review', view: 'pricereview', icon: 'refresh' },
  { label: 'Item types & fields', view: 'types', icon: 'sliders' },
  { label: 'Categories', view: 'categories', icon: 'folder' },
  { label: 'Collections', view: 'collections', icon: 'grid' },
  { label: 'Tags', view: 'tags', icon: 'tag' },
  { label: 'Locations', view: 'locations', icon: 'map-pin' },
  { label: 'Website', view: 'website', icon: 'globe' },
  { label: 'API keys', view: 'apikeys', icon: 'key' },
  { label: 'Webhooks', view: 'webhooks', icon: 'webhook' },
  { label: 'Imports', view: 'imports', icon: 'import' },
  { label: 'Settings', view: 'settings', icon: 'settings' },
];

interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onOpen?: () => void;
}

export default function CommandPalette({ open, onClose, onOpen }: CommandPaletteProps) {
  const { theme, isDark } = useTheme();
  const { colors, fontSize, fontWeight, radii, shadows } = theme;

  const [q, setQ] = useState('');

  // Reset query when palette opens
  useEffect(() => {
    if (open) setQ('');
  }, [open]);

  // Web: Cmd+K / Ctrl+K listener
  useEffect(() => {
    if (Platform.OS !== 'web') return;
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (open) {
          onClose();
        } else {
          onOpen?.();
        }
      }
      if (e.key === 'Escape' && open) {
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose, onOpen]);

  const ql = q.toLowerCase();

  // Live search against sk-catalog-api (GET /search), debounced.
  const [itemHits, setItemHits] = useState<ItemHit[]>([]);
  useEffect(() => {
    const term = q.trim();
    if (!term) { setItemHits([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const res = await searchItems({ q: term, limit: 6 });
        const rows = (res as any).items ?? [];
        if (!cancelled) {
          setItemHits(rows.map((r: any) => ({
            id: r.id ?? r.catalogItemId ?? r.item?.id,
            name: r.name ?? r.title ?? r.item?.title ?? r.skuOrReference ?? '(untitled)',
            ref: r.skuOrReference ?? r.ref ?? r.item?.skuOrReference ?? '',
            state: r.currentState ?? r.state,
          })));
        }
      } catch {
        if (!cancelled) setItemHits([]);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [q]);

  const navHits = NAV_ITEMS.filter(
    (n) => !q || n.label.toLowerCase().includes(ql),
  );

  const goItem = useCallback(
    (id: string) => {
      navigateApp('editor', { id });
      onClose();
    },
    [onClose],
  );

  const goNav = useCallback(
    (n: NavEntry) => {
      // n.view is a generic AppRouteName, so params is the union of all route
      // params; the cast aligns it with the value the helper expects.
      navigateApp(n.view, n.params as AppStackParamList[typeof n.view]);
      onClose();
    },
    [onClose],
  );

  if (!open) return null;

  // ── Styles ──────────────────────────────────────────────────

  const backdropStyle: ViewStyle = {
    position: 'absolute' as any,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.45)',
    alignItems: 'center',
    justifyContent: 'flex-start',
    paddingTop: Platform.OS === 'web' ? ('12vh' as any) : 60,
    zIndex: 100,
    ...Platform.select({ web: { position: 'fixed' } as any, default: {} }),
  };

  const containerStyle: ViewStyle = {
    width: 560,
    maxWidth: '100%' as any,
    backgroundColor: colors.card,
    borderRadius: radii.r4,
    ...shadows.lg,
    overflow: 'hidden',
  };

  const headerStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: 1,
    borderBottomColor: colors.line,
  };

  const inputStyle: TextStyle = {
    flex: 1,
    borderWidth: 0,
    backgroundColor: 'transparent',
    fontSize: fontSize.lg,
    color: colors.ink,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
    outlineWidth: 0 as any,
    outlineStyle: 'none' as any,
  };

  const kbdStyle: ViewStyle = {
    backgroundColor: colors.paper2,
    borderWidth: 1,
    borderColor: colors.line,
    borderRadius: radii.r1,
    paddingHorizontal: 7,
    paddingVertical: 3,
  };

  const kbdTextStyle: TextStyle = {
    fontFamily: theme.font.mono,
    fontSize: fontSize.xs,
    color: colors.ink3,
    includeFontPadding: false,
  };

  const menuLabelStyle: TextStyle = {
    fontSize: fontSize.xs,
    color: colors.ink4,
    textTransform: 'uppercase',
    letterSpacing: 0.05 * fontSize.xs,
    paddingHorizontal: 10,
    paddingTop: 6,
    paddingBottom: 3,
    fontWeight: fontWeight.semibold,
    includeFontPadding: false,
  };

  const menuItemStyle: ViewStyle = {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 8,
    paddingHorizontal: 10,
    borderRadius: radii.r2,
    ...Platform.select({
      web: { cursor: 'pointer' } as ViewStyle,
      default: {},
    }),
  };

  const menuItemTextStyle: TextStyle = {
    fontSize: fontSize.md,
    color: colors.ink2,
    fontFamily: theme.font.sans,
    includeFontPadding: false,
  };

  // ── Render ──────────────────────────────────────────────────

  const content = (
    <TouchableWithoutFeedback onPress={onClose}>
      <View style={backdropStyle}>
        <TouchableWithoutFeedback onPress={(e) => e.stopPropagation?.()}>
          <View style={containerStyle}>
            {/* Header — your CommonSearch component */}
            <View style={[headerStyle, { gap: 10 }]}>
              <View style={{ flex: 1 }}>
                <CommonSearch
                  autoFocus
                  placeholder="Search items or jump to…"
                  value={q}
                  onChange={setQ}
                  isDark={isDark}
                />
              </View>
              <View style={kbdStyle}>
                <Text style={kbdTextStyle}>esc</Text>
              </View>
            </View>

            {/* Body */}
            <ScrollView style={{ maxHeight: Platform.OS === 'web' ? ('50vh' as any) : 360 }} contentContainerStyle={{ padding: 8 }}>
              {/* Item hits */}
              {itemHits.length > 0 && (
                <Text style={menuLabelStyle}>Items</Text>
              )}
              {itemHits.map((it) => (
                <TouchableOpacity
                  key={it.id}
                  style={menuItemStyle}
                  onPress={() => goItem(it.id)}
                  activeOpacity={0.75}
                >
                  <Thumb emoji="📦" size={26} />
                  <View style={{ flex: 1, minWidth: 0 }}>
                    <Text
                      style={{
                        fontWeight: fontWeight.medium,
                        fontSize: fontSize.md,
                        color: colors.ink,
                        includeFontPadding: false,
                      }}
                      numberOfLines={1}
                    >
                      {it.name}
                    </Text>
                    <Text
                      style={{
                        fontFamily: theme.font.mono,
                        fontSize: fontSize.xs,
                        color: colors.ink3,
                        includeFontPadding: false,
                      }}
                      numberOfLines={1}
                    >
                      {it.ref}
                    </Text>
                  </View>
                  {it.state && <StateBadge state={it.state as any} />}
                </TouchableOpacity>
              ))}

              {/* Go to */}
              <Text style={menuLabelStyle}>Go to</Text>
              {navHits.map((n) => (
                <TouchableOpacity
                  key={n.label}
                  style={menuItemStyle}
                  onPress={() => goNav(n)}
                  activeOpacity={0.75}
                >
                  <Icon name={n.icon} size={16} color={colors.ink2} />
                  <Text style={menuItemTextStyle}>{n.label}</Text>
                </TouchableOpacity>
              ))}

              {/* Empty */}
              {itemHits.length === 0 && navHits.length === 0 && (
                <View style={{ padding: 16, alignItems: 'center' }}>
                  <Text style={{ color: colors.ink3, fontSize: fontSize.md, includeFontPadding: false }}>
                    No matches.
                  </Text>
                </View>
              )}
            </ScrollView>
          </View>
        </TouchableWithoutFeedback>
      </View>
    </TouchableWithoutFeedback>
  );

  if (Platform.OS === 'web') {
    // On web render as a fixed overlay in-tree
    return content;
  }

  // On native use RN Modal
  return (
    <Modal transparent visible={open} animationType="fade" onRequestClose={onClose}>
      {content}
    </Modal>
  );
}
